import React, { useMemo, useRef } from "react";
import { toast } from "react-toastify";

// ThatOpen / IFC.js
import * as FRAGS from "@thatopen/fragments";
import * as THREE from "three";

import { CameraTool } from "@treegram/tools-camera";
import { ImgLoader } from "@treegram/loaders-img";
import { FragmentLoader } from "@treegram/loaders-fragment";
import ViewerError from "@/components/viewer-error";

import useProjectFilePlanContext from "../context/use-project-file-plan-context.ts";
import ENDPOINTS from "@/services/endpoints.ts";

import KnownScaleContent from "@/pages/takeoff/context/project-organisation/models/known-scale-content.ts";
import UserDefinedContent from "@/pages/takeoff/context/project-organisation/models/user-defined-content.ts";
import BaseFileData from "@/pages/takeoff/context/project-organisation/models/base-file-data.ts";
import FragmentRecord from "@/pages/takeoff/context/project-measure/models/fragment-record.ts";

import ViewerLoading from "@/components/viewer-loading";
import Card from "@/components/card";
import useViewer, { createViewerTool, ViewerLoader, ViewerTool } from "@/hooks/viewer/use-viewer.ts";

const ViewerRight: React.FC = () => {
    const { urlFileRight: urlFile, fileRight: file, planFileDataRight: planFileData, fragsDataRight: fragsData } = useProjectFilePlanContext();

    const containerRef = useRef<HTMLDivElement>(null);

    /* --------- TOOLS CONFIG --------- */
    const tools = useMemo<ViewerTool[]>(() => [
        createViewerTool({
            key: "cameraTool",
            factory: (ctx) => new CameraTool(ctx.viewer),
            setup: (tool) => {
                tool.setNavigationMode("Plan");
            },
        }),
    ], []);

    /* --------- LOADERS CONFIG --------- */
    const planPdfLoader = useMemo<ViewerLoader<unknown, ImgLoader, BaseFileData>>(() => ({
            key: "plan-pdf-loader",
            factory: (ctx) => new ImgLoader(ctx.viewer.getComponents(), ctx.world),
            itemsToLoad: file ? [file] : undefined,
            fetchUrl: () => urlFile,
            name: (item) => item.name,
            getOptions: () => {
                let loaderOptions = {};
                if (planFileData.content instanceof KnownScaleContent) {
                    loaderOptions = {
                        format: planFileData.content.format,
                        orientation: planFileData.content.orientation,
                        scale: planFileData.content.scale,
                    };
                } else if (planFileData.content instanceof UserDefinedContent) {
                    loaderOptions = { definedScale: planFileData.content.scale };
                }
                return { useCoordinates: false, loaderOptions };
            },
            onItemLoaded: async (ctx, loader) => {
                const dimensionsCanvas = loader.getDimensionsCanvas();
                if (dimensionsCanvas) {
                    await ctx.viewer?.getCamera().controls.setLookAt(0, Math.max(dimensionsCanvas.height, dimensionsCanvas.width) * 0.9, 0, 0, 0, 0);
                }
            },
            onError: (_ctx, _loader, _item, error) => {
                toast.error(error.message, { autoClose: false });
            },
            enabled: urlFile != null,
        }), [file, urlFile, planFileData.content]);

    const fragLoader = useMemo<ViewerLoader<unknown, FragmentLoader, FragmentRecord>>(
        () => ({
            key: "frag-loader",
            factory: (ctx) => new FragmentLoader(ctx.viewer.getComponents(), ctx.world),
            itemsToLoad: fragsData,
            fetchUrl: (f) => ENDPOINTS.VIEWER_FILE(f.filePath),
            name: (f) => `${crypto.randomUUID()}_${f.name}`,
            options: {
                useCoordinates: false,
                loaderOptions: {
                    lodMode: FRAGS.LodMode.ALL_GEOMETRY,
                    origin: new THREE.Vector2(0, 0),
                    direction: new THREE.Vector2(1, 0),
                    showEdges: false
                },
            },
            onError: (_ctx, _loader, item) => {
                toast.error(`Erreur lors du chargement des fichiers 3D : ${item.name}`, { autoClose: false });
            },
            enabled: fragsData != null,
        }),
        [fragsData]
    );

    const viewerLoaders = useMemo<ViewerLoader[]>(
        () => [planPdfLoader, fragLoader],
        [planPdfLoader, fragLoader]
    );

    const { isLoading, loadingProgress, error, retry } = useViewer({
        containerRef,
        forceResize: true,
        viewerLoaders,
        tools,
    });

    return (
        <div className="relative w-full h-full" aria-busy={isLoading && !error}>
            <div ref={containerRef} className="w-full h-full" />
            <ViewerError error={error} retry={retry} />
            {file == null ? (
                <div className="absolute inset-0 z-12 flex items-center justify-center pointer-events-none">
                    <Card additionalClasses="px-4 py-2">
                        Sélectionner un plan pour l'afficher ici.
                    </Card>
                </div>
            ) : (
                isLoading && !error && <ViewerLoading progress={loadingProgress} />
            )}
        </div>
    );
};

export default ViewerRight;